// May this person start a production run in THIS business? (F02 D8)
//
// The answer is the server's, read the way the server reads it. The `runs`
// INSERT policy on prod is
//
//   runs_insert  WITH CHECK (current_tenant_role() = ANY ('ADMIN','MANAGER','SCALE_OPERATOR'))
//
// — mig 003_rls wrote it on the GLOBAL users.role (get_user_role()), and the
// write-policy sweep 20260629130000 swapped it, in place, to the role in the
// ACTIVE business. So the gate below asks for that same role
// (`rpc('current_tenant_role')`) and tests it against the same list. Before
// this, New Run had no gate at all: a QC technician (who holds the `runs`
// module) filled the picker, burned a run number at Create, and read the raw
// RLS sentence (verify-walk #11).
//
// While the role read is in flight — or if it fails, e.g. offline — the
// profile's own role (`useCurrentUser`, already the active business's role) is
// the stand-in, so the button never flickers away for a scale operator on shed
// wifi. A refusal the stand-in got wrong still reads as an operator sentence
// (RUN_CREATE_REFUSED below), never the RLS text.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import type { UserRole } from '@/types/database'

/** The role list in the live `runs_insert` policy, verbatim. */
export const RUN_STARTER_ROLES: readonly UserRole[] = ['ADMIN', 'MANAGER', 'SCALE_OPERATOR']

export const RUN_CREATE_REFUSED =
  "Your role in this business can't start a run. Ask a manager or scale operator to start it, or an admin to change your role."

/** friendlyError domain for the create path: the runs INSERT refusal. */
export const RUN_CREATE_DOMAIN: [RegExp, string][] = [
  [/row-level security policy for table "runs"/i, RUN_CREATE_REFUSED],
]

export function canStartRunAs(role: UserRole | null | undefined): boolean {
  return !!role && RUN_STARTER_ROLES.includes(role)
}

export function useCanStartRun(): boolean {
  const tenantId = useActiveTenantId()
  const me = useCurrentUser()
  const userId = me.data?.id ?? null
  const serverRole = useQuery({
    // The USER is in the key as well as the business: the query cache is
    // persisted (IndexedDB, 24h), so a key without it hands the next person
    // to sign in on a shared floor tablet the last person's role when they
    // start cold-offline.
    queryKey: ['run-insert-role', tenantId, userId],
    enabled: !!tenantId && !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await withReadTimeout(supabase.rpc('current_tenant_role'), 'Runs: current_tenant_role')
      if (error) throw error
      // null = no live membership in this business: the policy refuses that too.
      return (data ?? null) as UserRole | null
    },
  })
  // `data !== undefined` is the read that answered (readState's discriminator);
  // anything else falls back to the profile's role.
  const role = serverRole.data !== undefined ? serverRole.data : me.data?.role
  return canStartRunAs(role)
}
